import { Capabilities } from "@msys/common";
import { Theme } from "@mui/material";
import { noop } from "lodash-es";
import React from "react";
import { PermissionName } from "../../clients/graphqlTypes.js";
import { To } from "react-router-dom";

export interface SidebarGroup {
  name: string;
  items: SidebarItem[];
  capability?: Capabilities;
  anyCapabilities?: Capabilities[];
  permission?: PermissionName;
}

export interface SidebarItem {
  name: string;
  icon: React.ReactNode;
  label: string;
  path: To;
  activePaths?: string[];
  capability?: Capabilities;
  anyCapabilities?: Capabilities[];
  permission?: PermissionName;
}

export interface SectionSidebarConfig {
  isCollapsible: boolean;
  name: string;
  variant: "dense" | "regular";
  leftColumnWidth: keyof Theme["layout"]["columnWidth"];
}

const defaultSectionSidebarConfig: SectionSidebarConfig = {
  isCollapsible: false,
  name: "section",
  variant: "regular",
  leftColumnWidth: "xs",
};

interface SidebarContextType {
  isSidebarOpened: boolean;
  setIsSidebarOpened: (isOpened: boolean) => void;
  isSidebarPinned: boolean;
  toggleSidebarPinned: () => void;
  sidebarGroups: SidebarGroup[];
  setSidebarGroups: React.Dispatch<React.SetStateAction<SidebarGroup[]>>;
  sectionSidebarConfig: SectionSidebarConfig;
  setSectionSidebarConfig: React.Dispatch<
    React.SetStateAction<SectionSidebarConfig>
  >;
}

const SidebarContext = React.createContext<SidebarContextType>({
  isSidebarOpened: false,
  setIsSidebarOpened: noop,
  isSidebarPinned: false,
  toggleSidebarPinned: noop,
  sidebarGroups: [],
  setSidebarGroups: noop,
  sectionSidebarConfig: defaultSectionSidebarConfig,
  setSectionSidebarConfig: noop,
});

export const SidebarProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const [sidebarGroups, setSidebarGroups] = React.useState<SidebarGroup[]>([]);
  const [sectionSidebarConfig, setSectionSidebarConfig] =
    React.useState<SectionSidebarConfig>(defaultSectionSidebarConfig);
  const [isSidebarOpened, setIsSidebarOpened] = React.useState(false);
  const [isSidebarPinned, setSidebarPinned] = React.useState(false);
  const toggleSidebarPinned = React.useCallback(
    () => setSidebarPinned(isSidebarPinned => !isSidebarPinned),
    []
  );

  return (
    <SidebarContext.Provider
      value={{
        isSidebarOpened,
        setIsSidebarOpened,
        isSidebarPinned,
        toggleSidebarPinned,
        sidebarGroups,
        setSidebarGroups,
        sectionSidebarConfig,
        setSectionSidebarConfig,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebarContext = () => {
  return React.useContext(SidebarContext);
};

export const useSidebarGroups = (sidebarGroups: SidebarGroup[]) => {
  const { setSidebarGroups } = React.useContext(SidebarContext);

  return React.useEffect(() => {
    setSidebarGroups(sidebarGroups);
    return () => setSidebarGroups([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarGroups]);
};

export const useSectionSidebarConfig = (
  config?: Partial<SectionSidebarConfig>
) => {
  const { setSectionSidebarConfig } = React.useContext(SidebarContext);

  return React.useEffect(() => {
    setSectionSidebarConfig(current => ({
      ...current,
      ...defaultSectionSidebarConfig,
      ...config,
    }));

    return () => setSectionSidebarConfig(defaultSectionSidebarConfig);
  }, [config]);
};
